import * as React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import VoicemailIcon from '@mui/icons-material/Voicemail';
import CloseIcon from '@mui/icons-material/Close';
import SectionHeader from './SectionHeader';

// Hook to detect when element is visible in viewport
function useInView(threshold = 0.3) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Only trigger once
        }
      },
      { threshold }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}

// Animated number component with easing
function AnimatedNumber({ value, duration = 2000, delay = 0, isVisible }: { value: string; duration?: number; delay?: number; isVisible: boolean }) {
  const [displayValue, setDisplayValue] = React.useState(0);
  const isPercentage = value.includes('%');
  const isGreaterThan = value.startsWith('>');
  const numericValue = parseInt(value.replace(/[^0-9]/g, ''), 10);
  
  React.useEffect(() => {
    if (!isVisible) return;
    
    const startTime = Date.now() + delay;
    
    const animate = () => {
      const now = Date.now();
      if (now < startTime) {
        requestAnimationFrame(animate);
        return;
      }
      
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      
      setDisplayValue(Math.round(eased * numericValue));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [numericValue, duration, delay, isVisible]);

  return (
    <>{isGreaterThan && '>'}{displayValue}{isPercentage && '%'}</>
  );
}

// Animation: 5 voicemails appear, 4 get X marks (hung up)
function LostCallsAnimation({ isVisible }: { isVisible: boolean }) {
  const [step, setStep] = React.useState(0);
  const total = 5;
  const hungUpCount = 4;
  
  // Steps: 0=hidden, 1=voicemails appear, 2-5=X marks fill in
  React.useEffect(() => {
    if (!isVisible) return;
    
    const timers = [
      setTimeout(() => setStep(1), 500),
      setTimeout(() => setStep(2), 1400),
      setTimeout(() => setStep(3), 1750),
      setTimeout(() => setStep(4), 2100),
      setTimeout(() => setStep(5), 2450),
    ];
    return () => timers.forEach(clearTimeout);
  }, [isVisible]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 1,
        height: 48,
        mb: 1,
      }}
    >
      {Array.from({ length: total }).map((_, i) => {
        const willHangUp = i < hungUpCount;
        const hungUp = willHangUp && step >= (i + 2);
        const isVisible = step >= 1;
        
        return (
          <Box
            key={i}
            sx={{
              width: 32,
              height: 32,
              borderRadius: 2,
              backgroundColor: 'rgba(196, 185, 157, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'scale(1)' : 'scale(0.8)',
              transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              transitionDelay: `${i * 80}ms`,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Voicemail icon fades out, X fades in */}
            <VoicemailIcon 
              sx={{ 
                fontSize: 16, 
                color: '#6B5F4D',
                opacity: hungUp ? 0 : 1,
                transition: 'opacity 0.45s ease',
                position: 'absolute',
              }} 
            />
            {willHangUp && (
              <CloseIcon 
                sx={{ 
                  fontSize: 16, 
                  color: '#B5683C',
                  opacity: hungUp ? 1 : 0,
                  transition: 'opacity 0.45s ease',
                  position: 'absolute',
                }} 
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// Animation: Grid of people, 79% become highlighted
function FirstResponderAnimation({ isVisible }: { isVisible: boolean }) {
  const [highlighted, setHighlighted] = React.useState(false);
  
  const total = 48; // 8x6 grid
  const highlightCount = Math.round(total * 0.79); // 79% = ~38
  
  React.useEffect(() => {
    if (!isVisible) return;
    
    const timer = setTimeout(() => setHighlighted(true), 800);
    return () => clearTimeout(timer);
  }, [isVisible]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        height: 48,
        mb: 1,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gap: '3px',
        }}
      >
        {Array.from({ length: total }).map((_, i) => {
          const isHighlighted = highlighted && i < highlightCount;
          
          return (
            <Box
              key={i}
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: isHighlighted ? '#B5683C' : '#E8E2D8',
                transition: 'background-color 0.5s ease',
                transitionDelay: highlighted ? `${i * 25}ms` : '0ms',
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

// Animation: Simple boxes, first 3 fill (>50%)
function AfterHoursAnimation({ isVisible }: { isVisible: boolean }) {
  const [step, setStep] = React.useState(0);
  
  const total = 5;
  const afterHours = 3; // >50%
  
  // All appear, then first 3 fill
  React.useEffect(() => {
    if (!isVisible) return;
    
    const timers = [
      setTimeout(() => setStep(1), 500),  // All boxes appear
      setTimeout(() => setStep(2), 1300), // First 3 boxes fill
    ];
    return () => timers.forEach(clearTimeout);
  }, [isVisible]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 1,
        height: 48,
        mb: 1,
      }}
    >
      {Array.from({ length: total }).map((_, i) => {
        const isAfterHours = i < afterHours; // First 3 instead of last 3
        const isFilled = step >= 2 && isAfterHours;
        const isVisible = step >= 1;
        
        return (
          <Box
            key={i}
            sx={{
              width: 28,
              height: 28,
              borderRadius: 2,
              backgroundColor: isFilled ? '#B5683C' : 'rgba(196, 185, 157, 0.3)',
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'scale(1)' : 'scale(0.8)',
              transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              transitionDelay: step === 1 ? `${i * 80}ms` : (isAfterHours ? `${i * 130}ms` : '0ms'),
            }}
          />
        );
      })}
    </Box>
  );
}

const stats = [
  {
    value: '80%',
    description: 'of callers who hit voicemail',
    highlight: 'hang up immediately',
    Animation: LostCallsAnimation,
  },
  {
    value: '79%',
    description: 'of callers',
    highlight: 'hire the first law firm',
    suffix: 'who responds',
    Animation: FirstResponderAnimation,
  },
  {
    value: '>50%',
    description: 'of law firm leads come',
    highlight: 'after business hours',
    Animation: AfterHoursAnimation,
  },
];

// Individual stat card with visibility detection
function StatCard({ stat }: { stat: typeof stats[number] }) {
  const { ref, isVisible } = useInView(0.5);
  const AnimationComponent = stat.Animation;
  
  return (
    <Box
      ref={ref}
      sx={{
        flex: { sm: 1 },
        borderTop: '1px solid #E8E2D8',
        pt: 3,
      }}
    >
      {/* Animation */}
      <AnimationComponent isVisible={isVisible} />
      
      <Typography
        sx={{
          fontSize: { xs: '2.75rem', md: '3.5rem' },
          fontWeight: 500,
          letterSpacing: '-0.04em',
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.1,
          color: '#3E301E',
          mt: 1,
          mb: 1.5,
        }}
      >
        <AnimatedNumber value={stat.value} isVisible={isVisible} />
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: '#6B5F4D',
          lineHeight: 1.6,
          maxWidth: 300,
        }}
      >
        {stat.description}{' '}
        <Typography
          component="span"
          sx={{
            fontWeight: 600,
            color: '#3E301E',
          }}
        >
          {stat.highlight}
        </Typography>
        {stat.suffix && ` ${stat.suffix}`}
      </Typography>
    </Box>
  );
}

export default function Highlights() {
  return (
    <Box
      id="highlights"
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        backgroundColor: '#FDFCF9', // Soft Ivory
      }}
    >
      <Container maxWidth="lg">
        <SectionHeader
          title="Most callers won't leave a voicemail"
          subtitle="They hang up and call the next firm on the list. Whoever answers first usually gets the case."
        />

        {/* Stats Row */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 5, sm: 4, md: 6 },
          }}
        >
          {stats.map((stat, index) => (
            <StatCard key={index} stat={stat} />
          ))}
        </Box>
      </Container>
    </Box>
  );
}
